import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Copy, Check, Trash2, Clock, CheckCircle2, Link2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface Invite {
  id: string;
  email: string;
  display_name: string | null;
  invite_code: string;
  status: string;
  created_at: string;
  expires_at: string | null;
}

interface PendingInvitesProps {
  householdId: string;
  refreshKey: number;
}

const buildInviteLink = (code: string) =>
  `https://heirwise.app/login?invite=${encodeURIComponent(code)}`;

const PendingInvites = ({ householdId, refreshKey }: PendingInvitesProps) => {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase
        .from("household_invites")
        .select("*")
        .eq("household_id", householdId)
        .neq("status", "accepted")
        .order("created_at", { ascending: false });

      if (error) {
        toast.error("Failed to load household invites.");
        return;
      }

      setInvites((data || []));
    };

    void load();
  }, [householdId, refreshKey]);

  const copyValue = async (key: string, value: string, label: string) => {
    await navigator.clipboard.writeText(value);
    setCopiedKey(key);
    toast.success(`${label} copied.`);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const deleteInvite = async (id: string) => {
    const { error } = await supabase.from("household_invites").delete().eq("id", id);
    if (error) {
      toast.error(error.message || "Failed to remove invite.");
      return;
    }

    setInvites((prev) => prev.filter((invite) => invite.id !== id));
    toast.success("Invite removed.");
  };

  const isExpired = (invite: Invite) =>
    invite.status === "pending" && invite.expires_at && new Date(invite.expires_at) <= new Date();

  return (
    <div className="space-y-2 overflow-x-hidden">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Pending Invites</p>
      {invites.length === 0 ? (
        <div className="rounded-xl border border-border/60 bg-card/40 p-3 text-sm text-muted-foreground">
          No invites yet.
        </div>
      ) : (
        invites.map((invite) => {
          const expired = isExpired(invite);
          const status = expired ? "expired" : invite.status;
          return (
            <div key={invite.id} className="rounded-xl border border-border/60 bg-card/50 p-3 overflow-hidden">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{invite.display_name || invite.email}</p>
                    <p className="truncate text-xs text-muted-foreground">{invite.email}</p>
                    <span className={cn(
                      "mt-1 inline-flex items-center gap-1 text-[10px]",
                      status === "pending" && "text-secondary",
                      status === "accepted" && "text-primary",
                      status === "expired" && "text-destructive",
                    )}>
                      {status === "pending" && <Clock className="h-3 w-3" />}
                      {status === "accepted" && <CheckCircle2 className="h-3 w-3" />}
                      {status === "expired" && <AlertCircle className="h-3 w-3" />}
                      {status === "pending" ? "Pending" : status === "accepted" ? "Accepted" : "Expired"}
                    </span>
                  </div>

                  {(status === "pending" || status === "expired") && (
                    <Button variant="outline" size="sm" onClick={() => void deleteInvite(invite.id)} className="gap-1.5 text-destructive sm:w-auto">
                      <Trash2 className="h-3.5 w-3.5" />
                      Remove
                    </Button>
                  )}
                </div>

                {status === "pending" && (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div className="rounded-md border border-border/60 bg-background/40 p-2">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Code</p>
                      <div className="flex items-center gap-1.5">
                        <code className="flex-1 overflow-x-auto font-mono text-xs tracking-[0.2em] text-foreground">{invite.invite_code}</code>
                        <Button variant="ghost" size="sm" onClick={() => void copyValue(`code-${invite.id}`, invite.invite_code, "Code")} className="h-7 w-7 p-0">
                          {copiedKey === `code-${invite.id}` ? <Check className="h-3.5 w-3.5 text-primary" /> : <Copy className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    </div>
                    <div className="rounded-md border border-border/60 bg-background/40 p-2">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Link</p>
                      <div className="flex items-center gap-1.5">
                        <code className="flex-1 overflow-x-auto text-[11px] text-foreground">{buildInviteLink(invite.invite_code)}</code>
                        <Button variant="ghost" size="sm" onClick={() => void copyValue(`link-${invite.id}`, buildInviteLink(invite.invite_code), "Link")} className="h-7 w-7 p-0">
                          {copiedKey === `link-${invite.id}` ? <Check className="h-3.5 w-3.5 text-primary" /> : <Link2 className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default PendingInvites;
